import { type ReactElement, useCallback, useRef } from 'react'
import { useQueryState } from 'nuqs'
import { GOOGLE_MAP_STYLES } from 'app/components/map/styles'
import { Map as VisGlGoogleMap } from '@vis.gl/react-google-maps'
import type { GeoSearchWindow } from '@terros/common'
import { useDebounce, useToastError } from '@hooks'
import { useReportMap } from '@front/model'
import {
  boundsToBbox,
  ChooseDates,
  DEFAULT_MAP_CENTER,
  DEFAULT_MAP_ZOOM,
  GoogleMapProvider,
  type MapBounds,
  Page,
  parseAsMapBounds,
  useRangeQueryState,
} from '@components'
import { MemoizedContact } from './contact'
import { MemoizedCluster } from './cluster'
import { MemoizedAccount } from './account'
import { createFileRoute } from '@tanstack/react-router'

const INITIAL_BOUNDS = {
  south: 16.8645747608548,
  west: -134.62890625,
  north: 52.946711945696215,
  east: -65.37109375,
}

const MAP_BOUNDS_KEY = 'STATS_MAP_BOUNDS'

function StatsMapPage(): ReactElement {
  const { range, setRange } = useRangeQueryState('statsMapRange')
  const [start, end] = useDebounce(range, 1000)

  const [bounds, setBounds] = useQueryState('bounds', parseAsMapBounds.withDefault(INITIAL_BOUNDS))
  const debouncedBounds = useDebounce(bounds, 500)
  const { data, isFetching, error, refetch } = useReportMap({
    window: boundsToWindow(debouncedBounds),
    filter: {
      startDate: start?.valueOf(),
      endDate: end?.valueOf(),
    },
  })
  const mapRef = useRef<google.maps.Map | null>(null)
  const hasInitialized = useRef(false)
  const handleMapIdle = useCallback(
    (e: { map: google.maps.Map }) => {
      if (!mapRef.current) {
        mapRef.current = e.map
      }

      if (!hasInitialized.current) {
        let initialBounds = bounds

        if (!initialBounds) {
          try {
            const storedBounds = window.localStorage.getItem(MAP_BOUNDS_KEY)
            if (storedBounds) {
              const parsed = parseAsMapBounds.parse(storedBounds)
              if (parsed) {
                initialBounds = parsed
              }
            }
          } catch {
            // Ignore localStorage errors
          }
        }

        if (initialBounds && e.map) {
          const googleBounds = new google.maps.LatLngBounds(
            { lat: initialBounds.south, lng: initialBounds.west },
            { lat: initialBounds.north, lng: initialBounds.east }
          )
          e.map.fitBounds(googleBounds)
          setBounds(initialBounds)
          hasInitialized.current = true
          return
        }
        hasInitialized.current = true
      }

      const newBounds = e.map.getBounds()
      if (newBounds) {
        const bbox = boundsToBbox(newBounds)
        const boundsObj: MapBounds = {
          south: bbox[1], // minLat
          west: bbox[0], // minLng
          north: bbox[3], // maxLat
          east: bbox[2], // maxLng
        }
        setBounds(boundsObj)

        window.localStorage.setItem(MAP_BOUNDS_KEY, JSON.stringify(boundsObj))
        setBounds(boundsObj)
      }
    },
    [setBounds, bounds]
  )

  useToastError(error)

  const contacts = data?.contacts
  const accounts = data?.accounts
  const clusters = data?.clusters

  return (
    <GoogleMapProvider>
      <Page>
        <Page.Heading back isFetching={isFetching} refetch={refetch} title='Stats Map'>
          <ChooseDates range={range} setRange={setRange} />
        </Page.Heading>
        <Page.Content>
          <VisGlGoogleMap
            styles={GOOGLE_MAP_STYLES}
            defaultCenter={DEFAULT_MAP_CENTER}
            defaultZoom={DEFAULT_MAP_ZOOM}
            mapTypeControl={false}
            style={{ height: '100%', width: '100%' }}
            onIdle={handleMapIdle}
          >
            {clusters?.map((cluster) => (
              <MemoizedCluster cluster={cluster} key={cluster.geoHash} />
            ))}
            {contacts?.map((contact) => (
              <MemoizedContact contact={contact} key={contact.contactId} />
            ))}
            {accounts?.map((account) => (
              <MemoizedAccount account={account} key={account.accountId} />
            ))}
          </VisGlGoogleMap>
        </Page.Content>
      </Page>
    </GoogleMapProvider>
  )
}

const boundsToWindow = (bounds: { south: number; west: number; north: number; east: number }): GeoSearchWindow => ({
  topLeft: { latitude: bounds.north, longitude: bounds.west },
  bottomRight: { latitude: bounds.south, longitude: bounds.east },
})

export const Route = createFileRoute('/stats_/map/')({
  component: StatsMapPage,
})
